/* eslint-disable react-refresh/only-export-components */
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@eigtech/ui/lib/utils'

const badgeVariants = cva(
  'focus-visible:ring-ring focus-visible:ring-offset-background relative inline-flex w-fit shrink-0 items-center justify-center whitespace-nowrap border border-transparent font-medium outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg:not([class*=size-])]:size-3 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        outline: 'border-border dark:bg-input/32 bg-transparent',
        secondary: 'bg-secondary text-secondary-foreground',
        info: 'bg-info text-white',
        success: 'bg-success text-white',
        warning: 'bg-warning text-white',
        destructive: 'bg-destructive text-white',
        focus: 'bg-focus text-focus-foreground',
        invert: 'bg-invert text-invert-foreground',
        'primary-light': 'bg-primary/10 text-primary dark:bg-primary/20 border-none',
        'warning-light': 'bg-warning/10 text-warning-foreground dark:bg-warning/20 border-none',
        'success-light': 'bg-success/10 text-success-foreground dark:bg-success/20 border-none',
        'info-light': 'bg-info/10 text-info-foreground dark:bg-info/20 border-none',
        'destructive-light':
          'bg-destructive/10 text-destructive dark:bg-destructive/20 border-none',
        'invert-light': 'bg-invert/10 text-foreground dark:bg-invert/20 border-none',
        'focus-light': 'bg-focus/10 text-focus-foreground dark:bg-focus/20 border-none',
        'primary-outline': 'bg-background border-border text-primary dark:bg-input/30',
        'warning-outline': 'bg-background border-border text-warning-foreground dark:bg-input/30',
        'success-outline': 'bg-background border-border text-success-foreground dark:bg-input/30',
        'info-outline': 'bg-background border-border text-info-foreground dark:bg-input/30',
        'destructive-outline':
          'bg-background border-border text-destructive-foreground dark:bg-input/30',
        'invert-outline': 'bg-background border-border text-invert-foreground dark:bg-input/30',
        'focus-outline': 'bg-background border-border text-focus-foreground dark:bg-input/30',
      },
      size: {
        xs: 'py-0.25 h-4 min-w-4 gap-1 px-1 text-[0.6rem] leading-none',
        sm: 'py-0.25 h-4.5 min-w-4.5 gap-1 px-1 text-[0.625rem] leading-none',
        default: 'px-1.25 h-5 min-w-5 gap-1 py-0.5 text-xs',
        lg: 'h-5.5 min-w-5.5 gap-1 px-1.5 py-0.5 text-xs',
        xl: 'py-0.75 h-6 min-w-6 gap-1.5 px-2 text-sm',
      },
      /** `default`: per-theme radius. `full`: max radius per theme (Lyra stays `rounded-none`). */
      radius: {
        default: 'rounded-sm',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      radius: 'default',
    },
  }
)

interface BadgeProps extends useRender.ComponentProps<'span'> {
  variant?: VariantProps<typeof badgeVariants>['variant']
  size?: VariantProps<typeof badgeVariants>['size']
  radius?: VariantProps<typeof badgeVariants>['radius']
}

function Badge({ className, variant, size, radius, render, ...props }: BadgeProps) {
  const defaultProps = {
    'data-slot': 'badge',
    className: cn(badgeVariants({ variant, size, radius, className })),
  }

  return useRender({
    defaultTagName: 'span',
    render,
    props: mergeProps<'span'>(defaultProps, props),
  })
}

export { Badge, badgeVariants, type BadgeProps }
